import { Box, Button, Card, Tooltip } from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import DomainListTableRow from 'src/components/domains/domainListTableRow';
import { GeneralSelectMenu, GeneralTable } from 'src/components/general';
import { RHFTextField } from 'src/components/hook-form';
import { COOPERATION_ID_TABLE_HEAD, PROJECT_LIST_MODAL_TABLE_HEAD } from 'src/constants/tableHeads';
import useTable from 'src/hooks/useTable';
import {
  networkAffiliatedCompany,
  networkInsertMediaCooperation,
  networkRemoveMediaCooperation,
  networkUpdateMediaCooperation,
  useFetchMediaCooperationList,
  useFetchMediaGroupList,
  useFetchMediaGroupTypeList,
} from 'src/network/media-group';
import MediaCooperationTableRow from './MediaCooperationTableRow';
import { MEDIA_COOPERATION_LIST, MEDIA_COOPERATION_SCRIPT } from 'src/constants/queryKeys';
import useFormsModalStore from 'src/store/forms';
import { useQueryClient } from '@tanstack/react-query';
import Iconify from 'src/components/Iconify';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from 'src/routes/paths';
import GeneralFiltersAccordionWrapper from 'src/components/general/GeneralFiltersAccordionWrapper';
import LoadingButton from 'src/theme/overrides/LoadingButton';
import { useFetchProjectList } from 'src/network/application';
import { generateSelectMenuItems } from 'src/utils';

const MediaCooperation = () => {
  const { data: mediaGroupTypeResult } = useFetchMediaGroupTypeList();
  const [selectedTheme, setSelectedTheme] = useState('');
  const [mediaGroupList, setMediaGroupList] = useState('');
  const [affiliatedCompaniesList, setAffiliatedCompaniesList] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [buttonColor, setButtonColor] = useState('#ff5733');
  const [buttonColorSecond, setButtonColorSecond] = useState('#fff');

  const [selectedRow, setSelectedRow] = useState();

  const queryClient = useQueryClient();

  const [deleteLoading, setDeleteLoading] = useState(false);

  const { setModal } = useFormsModalStore();

  const [sticky, setSticky] = useState(false);

  const useTableOptions = useTable();
  const { page } = useTableOptions;

  const methods = useForm({
    defaultValues: {
      'data-playlist-id': 0,
      'space-between': '15',
      'data-width': '100',
      'button-color': '#ff5733',
      'button-text': 'دانلود ویدیو',
      'button-text-second': 'نمایش آنلاین',
      'space-between-320': '3',
      'space-between-480': '4',
      'space-between-768': '5',
      'space-between-1024': '8',
      'space-between-1280': '12',
      'data-margin-top': '0',
      'data-margin-bottom': '0',
      sticky: 'false',
    },
  });

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [groupList, setGroupList] = useState({
    AffiliatedCompanyID: 0,
    MediaGroupID: 0,
    MediaGroupType: 0,
  });

  const { data: tableList, isLoading: isLoadingMediaCooperationList, refetch } = useFetchMediaCooperationList(groupList);

  useEffect(() => {
    refetch();
  }, [groupList]);
  const [tableData, setTableData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (tableList) {
      setTableData(tableList?.data?.items || []);
    }
  }, [tableList]);

  useEffect(() => {
    getAffiliatedCompany();
  }, []);

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;
  const { data } = useFetchMediaGroupList(
    {
      ID: -1,
      Title: '',
      GroupID: selectedTheme,
    },
    0,
    1000
  );

  const getAffiliatedCompany = async () => {
    const data = await networkAffiliatedCompany();
    setAffiliatedCompaniesList(data?.data?.items);
  };

  const handleEditRow = (mediaCoperation) => {
    setModal({ data: mediaCoperation, isOpen: true, type: MEDIA_COOPERATION_LIST, title: 'ویرایش ', isEdit: true });
  };

  const handleDeleteRow = (mediaCoperationId) => {
    setDeleteLoading(true);
    networkRemoveMediaCooperation(mediaCoperationId)
      .then((res) => {
        const newTableData = tableData.filter((row) => row.MediaCooperationID !== mediaCoperationId);
        setTableData(newTableData);
        enqueueSnackbar('آیتم موردنظر با موفقیت حذف شد');

        queryClient.invalidateQueries({ queryKey: [MEDIA_COOPERATION_LIST] });
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setOpenConfirmDelete(false);
        setDeleteLoading(false);
      });
  };

  return (
    <>
      <div className="mb-5">
        <HeaderBreadcrumbs
          heading={'انتقال محتوا'}
          links={[
            { name: 'داشبورد', href: PATH_DASHBOARD.general.home },
            { name: 'انتقال محتوا', href: null },
          ]}
        />
        <Button
          variant="contained"
          startIcon={<Iconify icon={'eva:plus-fill'} />}
          onClick={() => setModal({ isOpen: true, type: MEDIA_COOPERATION_LIST, data: null, title: ' افزودن همکاری فروش' })}
        >
          افزودن همکاری در پخش ویدیو
        </Button>
      </div>
      <Card>
        <GeneralFiltersAccordionWrapper>
          <MediaCooperationFormFilter
            setGroupList={setGroupList}
            affiliatedCompaniesList={affiliatedCompaniesList}
            setAffiliatedCompaniesList={setAffiliatedCompaniesList}
            mediaGroupTypeResult={mediaGroupTypeResult}
            selectedTheme={selectedTheme}
            setSelectedTheme={setSelectedTheme}
            mediaGroupList={mediaGroupList}
            setMediaGroupList={setMediaGroupList}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            data={data}
          />
        </GeneralFiltersAccordionWrapper>
        <GeneralTable
          tableContainerSX={{ position: 'relative', maxHeight: { xs: 'calc(100dvh - 180px)', lg: 'calc(100dvh - 110px)' } }}
          rowID="MediaCooperationID"
          tableData={tableData}
          CustomTableRow={MediaCooperationTableRow}
          headLabel={COOPERATION_ID_TABLE_HEAD}
          isLoading={isLoadingMediaCooperationList}
          useTableOptions={useTableOptions}
          openConfirmDelete={openConfirmDelete}
          setOpenConfirmDelete={setOpenConfirmDelete}
          onEditRow={handleEditRow}
          onDeleteRow={handleDeleteRow}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
        />
      </Card>
    </>
  );
};

export default MediaCooperation;

export const MediaCooperationForms = () => {
  const { data: mediaGroupTypeResult } = useFetchMediaGroupTypeList();
  const [selectedTheme, setSelectedTheme] = useState('');
  const [mediaGroupList, setMediaGroupList] = useState('');
  const [affiliatedCompaniesList, setAffiliatedCompaniesList] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [buttonColor, setButtonColor] = useState('#ff5733');
  const [buttonColorSecond, setButtonColorSecond] = useState('#fff');
  const { data: projects } = useFetchProjectList();

  const [elData, setElData] = useState({});
  const [projectList, setProjectList] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  useEffect(() => {
    if (projects) {
      const result = projects?.data.items?.map((item) => {
        return { title: item.ApplicationTitle, appId: item.ApplicationID };
      });

      const projectMenuItems = generateSelectMenuItems(result, 'title', 'appId');

      setProjectList([...projectMenuItems]);
      // setSelectedProject(gatewayProfileDetails?.GatewayProfileApplicationID || projectMenuItems?.[0]?.id);
    }
  }, [projects]);

  const [selectedRow, setSelectedRow] = useState();

  const queryClient = useQueryClient();

  const [deleteLoading, setDeleteLoading] = useState(false);

  const { setModal, modal } = useFormsModalStore();

  const [sticky, setSticky] = useState(false);

  const useTableOptions = useTable();
  const { page } = useTableOptions;

  const getDatasEl = () => {
    const htmlString = modal.data?.MediaCooperationJsonData;

    // Convert the HTML string to a DOM element
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlString;
    const element = tempContainer.firstElementChild;

    const {
      playlistId,
      src,
      spaceBetween,
      width,
      buttonColor,
      buttonSecondColor,
      buttonText,
      buttonTextSecond,
      swiperBreakpoints,
      marginTop,
      marginBottom,
      sticky,
    } = {
      ...element.dataset,
      // swiperBreakpoints: JSON.parse(element.dataset.swiperBreakpoints),
    };

    setElData({
      playlistId,
      src,
      spaceBetween,
      width,
      buttonColor,
      buttonSecondColor,
      buttonText,
      buttonTextSecond,
      swiperBreakpoints,
      marginTop,
      marginBottom,
      sticky,
      buttonColorSecond,
    });
  };

  const methods = useForm({
    defaultValues: {
      'data-playlist-id': 0,
      'space-between': elData?.spaceBetween ? elData?.spaceBetween : '15',
      'data-width': '100',
      'button-color': '#ff5733',
      'button-text': 'دانلود ویدیو',
      'button-text-second': 'نمایش آنلاین',
      'space-between-320': '3',
      'space-between-480': '4',
      'space-between-768': '5',
      'space-between-1024': '8',
      'space-between-1280': '12',
      'data-margin-top': '0',
      'data-margin-bottom': '0',
      sticky: 'false',
    },
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (modal?.isEdit && modal) {
      setSelectedTheme(modal?.data?.MediaGroupMediaGroupTypeID);
      setMediaGroupList(modal?.data?.MediaCooperationMediaGroupID);
      setSelectedCompany(modal?.data?.AffiliatedCompanyID);
      setSelectedProject(modal?.data?.MediaCooperationApplicationID);
      getDatasEl();
    }
  }, [modal]);

  useEffect(() => {
    if (elData && modal?.isEdit) {
      // setSelectedProject

      reset({
        'data-playlist-id': elData.playlistId ?? 0,
        'space-between': elData.spaceBetween ?? '15',
        'data-width': parseInt(elData.width?.replace('%', '') ?? '100', 10),
        'button-color': elData.buttonColor ?? '#ff5733',
        'button-second-color': elData.buttonColorSecond ?? '#ff5733',
        'button-text': elData.buttonText ?? 'دانلود ویدیو',
        'button-text-second': elData.buttonTextSecond ?? 'نمایش آنلاین',
        'space-between-320': elData.swiperBreakpoints?.['320']?.slidesPerView ?? '3',
        'space-between-480': elData.swiperBreakpoints?.['480']?.slidesPerView ?? '4',
        'space-between-768': elData.swiperBreakpoints?.['768']?.slidesPerView ?? '5',
        'space-between-1024': elData.swiperBreakpoints?.['1024']?.slidesPerView ?? '8',
        'space-between-1280': elData.swiperBreakpoints?.['1280']?.slidesPerView ?? '12',
        'data-margin-top': parseInt(elData.marginTop?.replace('px', '') ?? '0', 10),
        'data-margin-bottom': parseInt(elData.marginBottom?.replace('px', '') ?? '0', 10),
        sticky: elData.sticky ?? 'false',
      });
      setButtonColor(elData.buttonColor);
      setButtonColorSecond(elData.buttonSecondColor);
    }
  }, [modal, elData]);

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  const { data: tableList, isLoading: isLoadingMediaCooperationList, refetch } = useFetchMediaCooperationList();
  const [status, setStatus] = useState(1);
  const statusList = [
    {
      id: 1,
      title: 'فعال',
    },
    {
      id: 2,
      title: 'غیر فعال',
    },
  ];

  const [tableData, setTableData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (tableList) {
      setTableData(tableList?.data?.items || []);
    }
  }, [tableList]);

  const { data } = useFetchMediaGroupList(
    {
      ID: -1,
      Title: '',
      GroupID: selectedTheme,
    },
    0,
    1000
  );

  const getAffiliatedCompany = async () => {
    const data = await networkAffiliatedCompany();
    setAffiliatedCompaniesList(data?.data?.items);
  };

  useEffect(() => {
    getAffiliatedCompany();
  }, []);

  const onSubmit = async (values) => {
    const output = `
    data-playlist-id="${mediaGroupList}"
    data-src="${selectedProject}"
    data-space-between="${values['space-between']}"
    data-width="${values['data-width']}%"
    data-button-color="${buttonColor}"
    data-button-second-color="${buttonColorSecond}"
    data-button-text="${values['button-text']}"
    data-button-text-second="${values['button-text-second']}"
    data-margin-top="${values['data-margin-top']}px"
    data-margin-bottom="${values['data-margin-bottom']}px"
    data-sticky="${sticky}"
    data-swiper-breakpoints='${JSON.stringify({
      320: { slidesPerView: values['space-between-320'] },
      480: { slidesPerView: values['space-between-480'] },
      768: { slidesPerView: values['space-between-768'] },
      1024: { slidesPerView: values['space-between-1024'] },
      1280: { slidesPerView: values['space-between-1280'] },
    })}'`;

    const element = `<div id="MeysamPlay" ${output}>
    </div>`;

    const payload = {
      MediaCooperationMediaGroupTypeID: selectedTheme,
      MediaCooperationAffiliatedCompanyID: selectedCompany,
      MediaCooperationMediaGroupID: mediaGroupList,
      MediaCooperationJsonData: element,
      MediaCooperationStatus: status,
      MediaCooperationApplicationID: selectedProject,
    };

    if (modal?.isEdit) {
      networkUpdateMediaCooperation(modal?.data?.MediaCooperationID, payload).then((res) => {
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          setTimeout(() => {
            refetch();
          }, 1000);
          setModal({ data: null, isOpen: false, type: MEDIA_COOPERATION_LIST, title: '' });
        } else {
          enqueueSnackbar(res.data?.items?.Desc, { variant: 'error' });
        }
      });
    } else {
      networkInsertMediaCooperation(payload).then((res) => {
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ثبت شد');
          setTimeout(() => {
            refetch();
          }, 1000);
          setModal({ data: null, isOpen: false, type: MEDIA_COOPERATION_LIST, title: '' });
        } else {
          enqueueSnackbar(res.data?.items?.Desc, { variant: 'error' });
        }
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            columnGap: 2,
            rowGap: 3,
            gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            width: '100%',
          }}
        >
          <GeneralSelectMenu
            label="نام محصول/پروژه"
            name="GatewayProfileApplicationID"
            options={projectList}
            value={selectedProject}
            onChangeValue={setSelectedProject}
          />

          <GeneralSelectMenu onch label="وضعیت" name="status" options={statusList} value={status} onChangeValue={setStatus} />

          <GeneralSelectMenu
            label="نام همکاری"
            name="item"
            options={
              affiliatedCompaniesList
                ? affiliatedCompaniesList?.map((item) => {
                    return {
                      ...item,
                      title: item?.AffiliatedCompanyName,
                      id: item?.AffiliatedCompanyID,
                    };
                  })
                : []
            }
            value={selectedCompany}
            onChangeValue={setSelectedCompany}
          />

          <GeneralSelectMenu
            onch
            label="انواع لیست پخش"
            name="playlist"
            options={
              mediaGroupTypeResult?.data?.items
                ? mediaGroupTypeResult?.data?.items?.slice(0, -2)?.map((item) => {
                    return {
                      ...item,
                      title: item?.MediaGroupTypeLable,
                      id: item?.MediaGroupTypeID,
                    };
                  })
                : []
            }
            value={selectedTheme}
            onChangeValue={setSelectedTheme}
          />

          {selectedTheme ? (
            <GeneralSelectMenu
              onch
              label="عنوان آیتم"
              name="item"
              options={
                data?.data?.items
                  ? data?.data?.items?.map((item) => {
                      return {
                        ...item,
                        title: item?.MediaGroupTitle,
                        id: item?.MediaGroupID,
                      };
                    })
                  : []
              }
              value={mediaGroupList}
              onChangeValue={setMediaGroupList}
            />
          ) : null}
        </Box>
        {mediaGroupList && (
          <>
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' },
                width: '100%',
              }}
            >
              <RHFTextField name="button-text" type="text" label="عنوان دکمه اول" />
              <MuiColorInput format="HEX" label="رنگ دکمه اول" value={buttonColor} onChange={(color) => setButtonColor(color)} />

              <RHFTextField name="button-text-second" type="text" label="عنوان دکمه دوم" />
              <MuiColorInput
                format="HEX"
                label="رنگ دکمه دوم"
                value={buttonColorSecond}
                onChange={(color) => setButtonColorSecond(color)}
              />

              <RHFTextField name="data-width" type="number" label="عرض swiper(%)" />
            </Box>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' },
                marginTop: '20px',
              }}
            >
              <RHFTextField value={3} name="space-between-320" type="text" label="swiper-breakpoints 320" />
              <RHFTextField name="space-between-480" type="text" label="swiper-breakpoints 480" />
              <RHFTextField name="space-between-768" type="text" label="swiper-breakpoints 768" />
              <RHFTextField name="space-between-1024" type="text" label="swiper-breakpoints 1024" />
              <RHFTextField name="space-between-1280" type="text" label="swiper-breakpoints 1280" />
              <RHFTextField name="space-between" type="number" label="space-between" />
              <RHFTextField name="data-margin-top" type="number" label="فاصله از بالا(px)" />
              <RHFTextField name="data-margin-bottom" type="number" label="فاصله از پایین(px)" />
              <GeneralSelectMenu
                onch
                label="sticky"
                name="sticky"
                options={[
                  {
                    title: 'بله',
                    id: 'true',
                  },
                  {
                    title: 'خیر',
                    id: 'false',
                  },
                ]}
                value={sticky}
                onChangeValue={setSticky}
              />
            </Box>
            <div className="text-left">
              <Button type="submit" onClick={() => {}} variant="contained" color="info">
                ذخیره
              </Button>
            </div>
          </>
        )}
      </form>
    </FormProvider>
  );
};

export const MediaCooperationFormFilter = ({
  onChangePage,
  filterItems,
  setFilterItems,
  affiliatedCompaniesList,
  setAffiliatedCompaniesList,
  mediaGroupTypeResult,
  selectedTheme,
  setSelectedTheme,
  mediaGroupList,
  setMediaGroupList,
  selectedCompany,
  setSelectedCompany,
  data,
  setGroupList,
}) => {
  const [status, setStatus] = useState(1);

  const statusList = [
    {
      id: 1,
      title: 'فعال',
    },
    {
      id: 2,
      title: 'غیر فعال',
    },
  ];

  const resetFilters = () => {
    setSelectedTheme(null);
    setMediaGroupList(null);
    setSelectedCompany(null);
    setStatus(1);
    setGroupList({
      AffiliatedCompanyID: 0,
      MediaGroupID: 0,
      MediaGroupType: 0,
    });
  };

  const onSubmit = async (values) => {
    const payload = {
      AffiliatedCompanyID: selectedCompany,
      MediaGroupID: mediaGroupList,
      MediaGroupType: selectedTheme,
    };
    setGroupList(payload);
  };

  const methods = useForm({
    defaultValues: {},
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  return (
    <>
      <FormProvider {...methods}>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            columnGap: 2,
            rowGap: 3,
            gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            width: '100%',
          }}
        >
          <GeneralSelectMenu
            label="نام همکاری"
            name="item"
            options={
              affiliatedCompaniesList
                ? affiliatedCompaniesList?.map((item) => {
                    return {
                      ...item,
                      title: item?.AffiliatedCompanyName,
                      id: item?.AffiliatedCompanyID,
                    };
                  })
                : []
            }
            value={selectedCompany}
            onChangeValue={setSelectedCompany}
          />
          <GeneralSelectMenu
            onch
            label="انواع لیست پخش"
            name="playlist"
            options={
              mediaGroupTypeResult?.data?.items
                ? mediaGroupTypeResult?.data?.items?.slice(0, -2)?.map((item) => {
                    return {
                      ...item,
                      title: item?.MediaGroupTypeLable,
                      id: item?.MediaGroupTypeID,
                    };
                  })
                : []
            }
            value={selectedTheme}
            onChangeValue={setSelectedTheme}
          />
          {selectedTheme ? (
            <GeneralSelectMenu
              onch
              label="عنوان آیتم"
              name="item"
              options={
                data?.data?.items
                  ? data?.data?.items?.map((item) => {
                      return {
                        ...item,
                        title: item?.MediaGroupTitle,
                        id: item?.MediaGroupID,
                      };
                    })
                  : []
              }
              value={mediaGroupList}
              onChangeValue={setMediaGroupList}
            />
          ) : (
            <span></span>
          )}

          <div className="text-left">
            <Button onClick={onSubmit} size="medium" type="submit" variant="contained" loading={isSubmitting} sx={{ height: '55px' }}>
              جستجو
            </Button>
            <Tooltip title="بازنشانی فیلترها">
              <Button sx={{ width: '50px', height: '55px', marginLeft: '10px' }} color="error" variant="outlined" onClick={resetFilters}>
                <Iconify icon={'eva:trash-2-outline'} sx={{ fontSize: '20px' }} />
              </Button>
            </Tooltip>
          </div>
        </Box>
      </FormProvider>
    </>
  );
};
